import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import type { ReactElement } from 'react'

import {
  adminDeviceBrowsingActivity,
  type AdminDeviceBrowsingActivityData,
  type BrowsingActivityRead,
} from '@/client'
import {
  adminClearDeviceBrowsingActivityMutation,
  adminDeviceBrowsingActivityQueryKey,
} from '@/client/@tanstack/react-query.gen'
import { ClearCaptureAction } from '@/components/features/clear-capture-action'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ADMIN_SUBMISSION_LOG_POLL_MS } from '@/lib/polling'

const PAGE_SIZE = 100
const UTC_OFFSET_SUFFIX = /(?:Z|[+-]\d{2}:?\d{2})$/i

type Cursor = Partial<Omit<NonNullable<AdminDeviceBrowsingActivityData['query']>, 'limit'>>

function parseServerTimestamp(value: string): Date {
  return new Date(UTC_OFFSET_SUFFIX.test(value) ? value : `${value}Z`)
}

function SubmissionLine({ item }: { item: BrowsingActivityRead }): ReactElement {
  const receivedAt = parseServerTimestamp(item.created_at)
  const hover = [item.url, item.title, format(receivedAt, 'yyyy-MM-dd HH:mm:ss')]
    .filter(Boolean)
    .join('\n')
  return (
    <li className="flex h-6 items-center gap-2 overflow-hidden px-2" title={hover}>
      <span className="text-muted-foreground shrink-0">{format(receivedAt, 'HH:mm:ss')}</span>
      <Badge variant={item.approved ? 'secondary' : 'destructive'} className="shrink-0 py-0">
        {item.approved ? 'allow' : 'block'}
      </Badge>
      {item.reason && <span className="shrink-0">{item.reason}</span>}
      {item.duration !== null && <span className="shrink-0">{`dwell ${item.duration}s`}</span>}
      <span className="min-w-0 truncate">{item.url}</span>
    </li>
  )
}

export function SubmissionLog({ deviceId }: { deviceId: string }): ReactElement {
  const queryClient = useQueryClient()
  const queryKey = adminDeviceBrowsingActivityQueryKey({
    path: { device_id: deviceId },
    query: { limit: PAGE_SIZE },
  })
  const log = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam, signal }) => {
      const { data } = await adminDeviceBrowsingActivity({
        path: { device_id: deviceId },
        query: { limit: PAGE_SIZE, ...pageParam },
        signal,
        throwOnError: true,
      })
      return data
    },
    initialPageParam: undefined as Cursor | undefined,
    getNextPageParam: (lastPage) => {
      if (lastPage.items.length < PAGE_SIZE) {
        return undefined
      }
      const last = lastPage.items[lastPage.items.length - 1]
      return { before_created_at: last.created_at, before_id: last.id }
    },
    refetchInterval: ADMIN_SUBMISSION_LOG_POLL_MS,
  })
  const clear = useMutation({
    ...adminClearDeviceBrowsingActivityMutation(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  })
  const items = log.data?.pages.flatMap((page) => page.items) ?? []

  return (
    <section className="space-y-3" aria-labelledby="submission-log-heading">
      <div className="flex items-center justify-between">
        <h2 id="submission-log-heading" className="text-lg font-semibold">
          Submission log
        </h2>
        {items.length > 0 && (
          <ClearCaptureAction
            trigger="Clear"
            title="Clear submission log?"
            description="Deletes all browsing activity for this device. Cannot be undone."
            confirmLabel="Clear"
            pending={clear.isPending}
            onConfirm={() => clear.mutate({ path: { device_id: deviceId } })}
          />
        )}
      </div>
      {log.isPending && <p className="text-muted-foreground text-sm">Loading…</p>}
      {log.isError && (
        <p role="alert" className="text-destructive text-sm">
          Submission log could not be loaded.
        </p>
      )}
      {log.isSuccess && items.length === 0 && (
        <p className="text-muted-foreground text-sm">No browsing submissions recorded.</p>
      )}
      {items.length > 0 && (
        <div className="rounded-md border">
          <div className="h-[calc(40*1.5rem)] overflow-y-auto font-mono text-xs leading-6">
            <ul>
              {items.map((item) => (
                <SubmissionLine key={item.id} item={item} />
              ))}
            </ul>
            {log.hasNextPage && (
              <div className="flex justify-center p-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void log.fetchNextPage()}
                  disabled={log.isFetchingNextPage}
                >
                  Load older
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  )
}
